import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Hash,
  MoreVertical,
  Eye,
  XCircle,
  MapPin,
  Car,
  Clock,
  AlertTriangle,
  CheckCircle,
  Check,
  Users,
  DollarSign,
  FileText,
  Shield,
  Heart,
  Home,
  Ship,
  Zap,
  Plane,
} from "lucide-react";
import PolicyDatesInfo from "./PolicyDatesInfo";
import { formatMoney } from "../../utils/formatMoney";

// Insurance type style mapping
const insuranceTypeStyleMap = {
  travel_insurance: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    ring: "ring-blue-200",
    dot: "bg-blue-500",
    icon: Plane,
    gradient: "from-blue-500 to-indigo-600",
    accent: "blue",
  },
  life_insurance: {
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    ring: "ring-indigo-200",
    dot: "bg-indigo-500",
    icon: Shield,
    gradient: "from-indigo-500 to-blue-600",
    accent: "indigo",
  },
  general_insurance: {
    bg: "bg-purple-50",
    text: "text-purple-700",
    ring: "ring-purple-200",
    dot: "bg-purple-500",
    icon: FileText,
    gradient: "from-purple-500 to-violet-600",
    accent: "purple",
  },
  // General Insurance subcategories
  health_insurance: {
    bg: "bg-pink-50",
    text: "text-pink-700",
    ring: "ring-pink-200",
    dot: "bg-pink-500",
    icon: Heart,
    gradient: "from-pink-500 to-rose-600",
    accent: "pink",
  },
  home_insurance: {
    bg: "bg-purple-50",
    text: "text-purple-700",
    ring: "ring-purple-200",
    dot: "bg-purple-500",
    icon: Home,
    gradient: "from-purple-500 to-violet-600",
    accent: "purple",
  },
  accident_insurance: {
    bg: "bg-red-50",
    text: "text-red-700",
    ring: "ring-red-200",
    dot: "bg-red-500",
    icon: AlertTriangle,
    gradient: "from-red-500 to-rose-600",
    accent: "red",
  },
  fire_insurance: {
    bg: "bg-orange-50",
    text: "text-orange-700",
    ring: "ring-orange-200",
    dot: "bg-orange-500",
    icon: Zap,
    gradient: "from-orange-500 to-red-600",
    accent: "orange",
  },
  marine_insurance: {
    bg: "bg-cyan-50",
    text: "text-cyan-700",
    ring: "ring-cyan-200",
    dot: "bg-cyan-500",
    icon: Ship,
    gradient: "from-cyan-500 to-blue-600",
    accent: "cyan",
  },
  liability_insurance: {
    bg: "bg-teal-50",
    text: "text-teal-700",
    ring: "ring-teal-200",
    dot: "bg-teal-500",
    icon: Shield,
    gradient: "from-teal-500 to-green-600",
    accent: "teal",
  },
};

// Travel policy type styles
const travelPolicyTypeStyleMap = {
  intercity_travel: {
    label: "Intercity Travel",
    icon: Car,
  },
  daily_commuter: {
    label: "Daily Commuter",
    icon: Users,
  },
  okada_rider: {
    label: "Motor Rider",
    icon: Car,
  },
  taxi_and_bolt: {
    label: "Taxi & Bolt",
    icon: Car,
  },
};

const PolicyCard = ({ policy, onView, onCancel, onClaim, className = "" }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Extract data from new structure
  const purchasedData = useMemo(
    () => policy?.purchased_policy_data || {},
    [policy]
  );
  const insurancePolicy = useMemo(
    () => policy?.insurance_policy || {},
    [policy]
  );
  const customer = useMemo(() => policy?.customer || {}, [policy]);

  // Determine insurance type
  const insuranceType = useMemo(() => {
    if (purchasedData?.insurance_type) {
      return purchasedData.insurance_type;
    }
    return insurancePolicy?.short_description || "travel_insurance";
  }, [purchasedData, insurancePolicy]);

  // Determine policy type for travel insurance
  const policyType = useMemo(() => {
    if (purchasedData?.policy_type) {
      return purchasedData.policy_type;
    }
    return insurancePolicy?.policy_type;
  }, [purchasedData, insurancePolicy]);

  // Get style based on insurance type and subcategory
  const getInsuranceStyles = useMemo(() => {
    // Check for general insurance subcategory first
    if (insuranceType === "general_insurance" && policyType) {
      return (
        insuranceTypeStyleMap[policyType] ||
        insuranceTypeStyleMap.general_insurance
      );
    }
    return (
      insuranceTypeStyleMap[insuranceType] ||
      insuranceTypeStyleMap.travel_insurance
    );
  }, [insuranceType, policyType]);

  const styles = getInsuranceStyles;
  const InsuranceIcon = styles.icon;

  // Determine if it's travel, life, or general insurance
  const isTravelInsurance = insuranceType === "travel_insurance";
  const isTripSecureInsurance = insuranceType === "tripsecure_insurance";
  const isLifeInsurance = insuranceType === "life_insurance";
  const isGeneralInsurance = insuranceType === "general_insurance";

  // Get policy name
  const policyName = useMemo(() => {
    if (isLifeInsurance || isGeneralInsurance) {
      return (
        purchasedData?.product_name ||
        insurancePolicy?.policy_name ||
        "Insurance Policy"
      );
    }
    return insurancePolicy?.policy_name || "TripSecure Policy";
  }, [isLifeInsurance, isGeneralInsurance, purchasedData, insurancePolicy]);

  // Get coverage duration info
  const coverageDuration = useMemo(() => {
    if (
      (isTravelInsurance || isTripSecureInsurance) &&
      purchasedData?.coverageDuration
    ) {
      return purchasedData.coverageDuration;
    }
    // For okada_rider and daily_commuter, check coverage_durations in insurance_policy
    if (
      isTripSecureInsurance &&
      (policyType === "okada_rider" || policyType === "daily_commuter") &&
      insurancePolicy?.coverage_durations?.length > 0
    ) {
      return insurancePolicy.coverage_durations[0];
    }
    return null;
  }, [
    isTravelInsurance,
    isTripSecureInsurance,
    purchasedData,
    policyType,
    insurancePolicy,
  ]);

  // Check if travel_insurance uses string duration format (not coverageDuration object)
  const hasStringDuration = useMemo(() => {
    return (
      isTravelInsurance &&
      purchasedData?.duration &&
      typeof purchasedData.duration === "string" &&
      !purchasedData?.coverageDuration
    );
  }, [isTravelInsurance, purchasedData]);

  // Helper to format duration (e.g., "1 monthly" -> "1 month", "2 yearly" -> "2 years")
  const formatDuration = (duration) => {
    if (!duration) return "N/A";

    const durationStr = String(duration).toLowerCase();

    // Match pattern like "1 monthly", "2 yearly", "3 weekly", etc.
    const match = durationStr.match(
      /^(\d+)\s*(daily|weekly|monthly|yearly|day|week|month|year)s?$/
    );

    if (!match) return duration; // Return as-is if no match

    const value = parseInt(match[1]);
    let unit = match[2];

    // Normalize unit names
    const unitMap = {
      daily: "day",
      weekly: "week",
      monthly: "month",
      yearly: "year",
    };

    unit = unitMap[unit] || unit;

    // Pluralize if value > 1
    if (value > 1) {
      unit = unit + "s";
    }

    return `${value} ${unit}`;
  };

  // Format duration label
  const formatDurationLabel = useMemo(() => {
    // For travel_insurance with string duration (e.g., "3 hours")
    if (hasStringDuration) {
      return formatDuration(purchasedData.duration);
    }
    if ((isTravelInsurance || isTripSecureInsurance) && coverageDuration) {
      if (coverageDuration.display_unit && coverageDuration.display_value) {
        const unit = coverageDuration.display_unit;
        const value = coverageDuration.display_value;
        return `${value} ${value === 1 ? unit.slice(0, -1) : unit}`;
      }
      if (coverageDuration.hours) {
        const hours = coverageDuration.hours;
        return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
      }
    }
    if (isLifeInsurance || isGeneralInsurance) {
      return formatDuration(purchasedData?.duration) || "N/A";
    }
    return "N/A";
  }, [
    isTravelInsurance,
    isTripSecureInsurance,
    isLifeInsurance,
    isGeneralInsurance,
    coverageDuration,
    purchasedData,
    hasStringDuration,
  ]);

  // Get price/premium
  const getPrice = useMemo(() => {
    // For travel_insurance with string duration, use premium from purchased_policy_data
    if (hasStringDuration) {
      return purchasedData?.premium || 0;
    }
    if ((isTravelInsurance || isTripSecureInsurance) && coverageDuration) {
      return coverageDuration.price || 0;
    }
    if (isLifeInsurance || isGeneralInsurance) {
      return purchasedData?.premium || 0;
    }
    return 0;
  }, [
    isTravelInsurance,
    isTripSecureInsurance,
    isLifeInsurance,
    isGeneralInsurance,
    coverageDuration,
    purchasedData,
    hasStringDuration,
  ]);

  // Get premium duration (payment term) for Life, General, and Travel Insurance
  const getPremiumDuration = useMemo(() => {
    if (isLifeInsurance || isGeneralInsurance || (isTravelInsurance && hasStringDuration)) {
      const duration = purchasedData?.premium_duration || "monthly";
      // Normalize duration for display (yearly -> year, monthly -> month, etc.)
      const durationMap = {
        'yearly': 'year',
        'monthly': 'month',
        'weekly': 'week',
        'daily': 'day'
      };
      return durationMap[duration.toLowerCase()] || duration;
    }
    return null;
  }, [isLifeInsurance, isGeneralInsurance, isTravelInsurance, hasStringDuration, purchasedData]);

  // Get policy type label
  const getPolicyTypeLabel = useMemo(() => {
    if (isTravelInsurance || isTripSecureInsurance) {
      return (
        travelPolicyTypeStyleMap[policyType]?.label ||
        policyType
          ?.replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase()) ||
        (isTripSecureInsurance ? "TripSecure Insurance" : "Travel Insurance")
      );
    }
    if (isLifeInsurance) {
      return "Life Insurance";
    }
    if (isGeneralInsurance) {
      const subcategoryMap = {
        health_insurance: "Health Insurance",
        home_insurance: "Home Insurance",
        accident_insurance: "Accident Insurance",
        fire_insurance: "Fire Insurance",
        marine_insurance: "Marine Insurance",
        liability_insurance: "Liability Insurance",
      };
      return subcategoryMap[policyType] || "General Insurance";
    }
    return "Insurance Policy";
  }, [
    isTravelInsurance,
    isTripSecureInsurance,
    isLifeInsurance,
    isGeneralInsurance,
    policyType,
  ]);

  // Policy status – bold badge colors by status
  const policyStatus = policy?.policy_status;
  const isActive = policyStatus === null || policyStatus === "active";
  const isCancelPending = policyStatus === "cancel_pending";
  const isCancelled =
    policyStatus === "inactive" || policyStatus === "cancelled";

  const statusBadgeStyles = useMemo(() => {
    if (isActive)
      return {
        bg: "bg-emerald-100",
        text: "text-emerald-800",
        ring: "ring-emerald-300",
        dot: "bg-emerald-500",
      };
    if (isCancelPending)
      return {
        bg: "bg-amber-100",
        text: "text-amber-900",
        ring: "ring-amber-400",
        dot: "bg-amber-500",
      };
    if (isCancelled)
      return {
        bg: "bg-red-100",
        text: "text-red-800",
        ring: "ring-red-300",
        dot: "bg-red-500",
      };
    return {
      bg: "bg-gray-200",
      text: "text-gray-700",
      ring: "ring-gray-300",
      dot: "bg-gray-500",
    };
  }, [isActive, isCancelPending, isCancelled]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className={`flex overflow-visible relative flex-col h-full bg-white rounded-xl md:rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 group hover:shadow-xl ${
        open ? "z-20" : ""
      } ${
        styles.accent === "blue"
          ? "hover:shadow-blue-500/10"
          : styles.accent === "indigo"
          ? "hover:shadow-indigo-500/10"
          : styles.accent === "purple"
          ? "hover:shadow-purple-500/10"
          : styles.accent === "pink"
          ? "hover:shadow-pink-500/10"
          : styles.accent === "red"
          ? "hover:shadow-red-500/10"
          : styles.accent === "orange"
          ? "hover:shadow-orange-500/10"
          : styles.accent === "cyan"
          ? "hover:shadow-cyan-500/10"
          : styles.accent === "teal"
          ? "hover:shadow-teal-500/10"
          : "hover:shadow-gray-500/10"
      } ${className}`}
    >
      <div className="overflow-hidden absolute inset-0 pointer-events-none rounded-xl md:rounded-2xl">
        <div
          className={`absolute -right-10 -top-16 w-40 h-40 bg-gradient-to-br ${styles.gradient} rounded-full blur-2xl opacity-10`}
        />
        <div
          className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${styles.gradient} opacity-60`}
        />
      </div>

      <div className="flex relative flex-col p-3 h-full md:p-5">
        {/* Header */}
        <div className="flex gap-2 justify-between items-start mb-3 md:gap-3 md:mb-4">
          <div className="flex flex-1 gap-2 items-start min-w-0 md:gap-3">
            <div
              className={`grid place-items-center flex-shrink-0 w-8 h-8 text-white bg-gradient-to-br md:w-12 md:h-12 ${
                styles.gradient
              } rounded-lg md:rounded-xl ring-2 shadow-lg ${
                styles.accent === "blue"
                  ? "ring-blue-500/30"
                  : styles.accent === "indigo"
                  ? "ring-indigo-500/30"
                  : styles.accent === "purple"
                  ? "ring-purple-500/30"
                  : styles.accent === "pink"
                  ? "ring-pink-500/30"
                  : styles.accent === "red"
                  ? "ring-red-500/30"
                  : styles.accent === "orange"
                  ? "ring-orange-500/30"
                  : styles.accent === "cyan"
                  ? "ring-cyan-500/30"
                  : styles.accent === "teal"
                  ? "ring-teal-500/30"
                  : "ring-gray-500/30"
              }`}
            >
              <InsuranceIcon className="w-3.5 h-3.5 md:w-5 md:h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3
                className="mb-0.5 text-sm font-bold leading-tight text-gray-900 truncate md:mb-1 md:text-lg"
                title={policyName}
              >
                {policyName}
              </h3>
              <p
                className="mb-1 text-[10px] text-gray-600 truncate md:mb-2 md:text-xs"
                title={getPolicyTypeLabel}
              >
                {getPolicyTypeLabel}
              </p>
              <div className="hidden gap-2 items-center text-xs text-gray-500 sm:flex">
                <Hash size={10} />
                <span className="font-mono text-xs">
                  #{policy?.insurance_policy_slug?.slice(-8) || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Actions Menu */}
          <div className="relative z-50 flex-shrink-0">
            <button
              ref={buttonRef}
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="grid place-items-center w-9 h-9 text-gray-600 rounded-lg ring-1 ring-gray-200 backdrop-blur transition-colors hover:ring-orange-300 hover:text-orange-700 bg-white/70"
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <MoreVertical size={18} />
            </button>

            <AnimatePresence>
              {open && (
                <motion.div
                  ref={menuRef}
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="overflow-hidden absolute right-0 top-10 z-50 w-40 bg-white rounded-xl border border-gray-100 shadow-xl md:w-48"
                  role="menu"
                >
                  <div className="p-1">
                    <button
                      onClick={() => {
                        setOpen(false);
                        onView && onView();
                      }}
                      className="flex gap-3 items-center px-4 py-3 w-full text-sm text-gray-700 rounded-lg transition-colors hover:bg-blue-50 hover:text-blue-700"
                      role="menuitem"
                    >
                      <Eye size={16} />
                      <span>View Details</span>
                    </button>
                    <button
                      onClick={() => {
                        setOpen(false);
                        onClaim && onClaim();
                      }}
                      className="flex gap-3 items-center px-4 py-3 w-full text-sm text-emerald-600 rounded-lg transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                      role="menuitem"
                    >
                      <FileText size={16} />
                      <span>Claim Policy</span>
                    </button>
                    <button
                      onClick={() => {
                        setOpen(false);
                        onCancel && onCancel();
                      }}
                      className="flex gap-3 items-center px-4 py-3 w-full text-sm text-red-600 rounded-lg transition-colors hover:bg-red-50 hover:text-red-700"
                      role="menuitem"
                    >
                      <XCircle size={16} />
                      <span>Cancel Policy</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-grow space-y-2.5 md:space-y-4">
          {/* Travel Insurance / TripSecure Insurance Details */}
          {(isTravelInsurance || isTripSecureInsurance) && purchasedData && (
            <>
              <div className="grid grid-cols-2 gap-1.5 md:gap-3">
                <div
                  className={`p-1.5 md:p-3 ${styles.bg} rounded-md md:rounded-xl border ${
                    styles.accent === "blue"
                      ? "border-blue-200/50"
                      : styles.accent === "indigo"
                      ? "border-indigo-200/50"
                      : styles.accent === "purple"
                      ? "border-purple-200/50"
                      : styles.accent === "pink"
                      ? "border-pink-200/50"
                      : styles.accent === "red"
                      ? "border-red-200/50"
                      : styles.accent === "orange"
                      ? "border-orange-200/50"
                      : styles.accent === "cyan"
                      ? "border-cyan-200/50"
                      : styles.accent === "teal"
                      ? "border-teal-200/50"
                      : "border-gray-200/50"
                  }`}
                >
                  <div className="flex gap-1 items-center mb-0.5 md:gap-1.5 md:mb-1.5">
                    <Clock className={`w-3 h-3 md:w-3.5 md:h-3.5 ${styles.text}`} />
                    <span
                      className={`text-[9px] font-semibold tracking-wide ${styles.text} uppercase md:text-[10px]`}
                    >
                      Duration
                    </span>
                  </div>
                  <p className="text-[11px] font-bold text-gray-900 truncate mb-0 md:text-sm md:mb-0.5">
                    {formatDurationLabel}
                  </p>
                  <div className="flex items-baseline gap-0.5 md:gap-1 min-w-0">
                    <p className={`text-[10px] ${styles.text} font-semibold truncate md:text-xs`}>
                      {formatMoney(getPrice)}
                    </p>
                    {getPremiumDuration && (
                      <p className="text-[9px] text-gray-500 capitalize truncate md:text-[10px]">
                        per {getPremiumDuration}
                      </p>
                    )}
                  </div>
                </div>

                {/* Route info - only show for intercity_travel, not okada_rider/daily_commuter or travel_insurance with string duration */}
                {policyType !== "okada_rider" &&
                  policyType !== "daily_commuter" &&
                  !hasStringDuration && (
                  <div
                    className={`p-1.5 md:p-3 ${styles.bg} rounded-md md:rounded-xl border ${
                      styles.accent === "blue"
                        ? "border-blue-200/50"
                        : styles.accent === "indigo"
                        ? "border-indigo-200/50"
                        : styles.accent === "purple"
                        ? "border-purple-200/50"
                        : styles.accent === "pink"
                        ? "border-pink-200/50"
                        : styles.accent === "red"
                        ? "border-red-200/50"
                        : styles.accent === "orange"
                        ? "border-orange-200/50"
                        : styles.accent === "cyan"
                        ? "border-cyan-200/50"
                        : styles.accent === "teal"
                        ? "border-teal-200/50"
                        : "border-gray-200/50"
                    }`}
                  >
                    <div className="flex gap-1 items-center mb-0.5 md:gap-1.5 md:mb-1.5">
                      <MapPin className={`w-3 h-3 md:w-3.5 md:h-3.5 ${styles.text}`} />
                      <span
                        className={`text-[9px] font-semibold tracking-wide ${styles.text} uppercase md:text-[10px]`}
                      >
                        Route
                      </span>
                    </div>
                    <p
                      className="text-[11px] font-bold text-gray-900 truncate md:text-sm"
                      title={
                        purchasedData.routeDestination ||
                        coverageDuration?.route_destination ||
                        "N/A"
                      }
                    >
                      {purchasedData.routeDestination ||
                        coverageDuration?.route_destination ||
                        "N/A"}
                    </p>
                    {coverageDuration?.route_type && (
                      <p className={`text-[10px] ${styles.text} capitalize truncate md:text-xs`}>
                        {coverageDuration.route_type}
                      </p>
                    )}
                  </div>
                )}

                {/* Product name for travel_insurance with string duration */}
                {hasStringDuration && (
                  <div
                    className={`p-1.5 md:p-3 ${styles.bg} rounded-md md:rounded-xl border ${
                      styles.accent === "blue"
                        ? "border-blue-200/50"
                        : styles.accent === "indigo"
                        ? "border-indigo-200/50"
                        : styles.accent === "purple"
                        ? "border-purple-200/50"
                        : styles.accent === "pink"
                        ? "border-pink-200/50"
                        : styles.accent === "red"
                        ? "border-red-200/50"
                        : styles.accent === "orange"
                        ? "border-orange-200/50"
                        : styles.accent === "cyan"
                        ? "border-cyan-200/50"
                        : styles.accent === "teal"
                        ? "border-teal-200/50"
                        : "border-gray-200/50"
                    }`}
                  >
                    <div className="flex gap-1 items-center mb-0.5 md:gap-1.5 md:mb-1.5">
                      <FileText className={`w-3 h-3 md:w-3.5 md:h-3.5 ${styles.text}`} />
                      <span
                        className={`text-[9px] font-semibold tracking-wide ${styles.text} uppercase md:text-[10px]`}
                      >
                        Product
                      </span>
                    </div>
                    <p
                      className="text-[11px] font-bold text-gray-900 truncate md:text-sm"
                      title={purchasedData.product_name || "N/A"}
                    >
                      {purchasedData.product_name || "N/A"}
                    </p>
                  </div>
                )}

                {/* Bike / vehicle info for moto rider and daily commuter */}
                {(policyType === "okada_rider" || policyType === "daily_commuter") && (
                  <div
                    className={`p-1.5 md:p-3 ${styles.bg} rounded-md md:rounded-xl border ${
                      styles.accent === "blue"
                        ? "border-blue-200/50"
                        : styles.accent === "indigo"
                        ? "border-indigo-200/50"
                        : styles.accent === "purple"
                        ? "border-purple-200/50"
                        : styles.accent === "pink"
                        ? "border-pink-200/50"
                        : styles.accent === "red"
                        ? "border-red-200/50"
                        : styles.accent === "orange"
                        ? "border-orange-200/50"
                        : styles.accent === "cyan"
                        ? "border-cyan-200/50"
                        : styles.accent === "teal"
                        ? "border-teal-200/50"
                        : "border-gray-200/50"
                    }`}
                  >
                    <div className="flex gap-1 items-center mb-0.5 md:gap-1.5 md:mb-1.5">
                      <Car className={`w-3 h-3 md:w-3.5 md:h-3.5 ${styles.text}`} />
                      <span
                        className={`text-[9px] font-semibold tracking-wide ${styles.text} uppercase md:text-[10px]`}
                      >
                        {policyType === "daily_commuter" ? "Vehicle" : "Bike"}
                      </span>
                    </div>
                    <p
                      className="text-[11px] font-bold text-gray-900 truncate md:text-sm"
                      title={purchasedData.vehicleName || purchasedData.bikeName || "N/A"}
                    >
                      {purchasedData.vehicleName || purchasedData.bikeName || "N/A"}
                    </p>
                    {purchasedData.licensePlate && (
                      <p className={`text-[10px] ${styles.text} font-mono truncate md:text-xs`}>
                        {purchasedData.licensePlate}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Trip Details - only for intercity_travel, not travel_insurance with string duration */}
              {policyType === "intercity_travel" &&
                !hasStringDuration &&
                (purchasedData.departureCity ||
                  purchasedData.destinationCity) && (
                  <div
                    className={`hidden md:block p-2 md:p-3 ${styles.bg} rounded-lg md:rounded-xl border ${
                      styles.accent === "blue"
                        ? "border-blue-200/50"
                        : styles.accent === "indigo"
                        ? "border-indigo-200/50"
                        : styles.accent === "purple"
                        ? "border-purple-200/50"
                        : styles.accent === "pink"
                        ? "border-pink-200/50"
                        : styles.accent === "red"
                        ? "border-red-200/50"
                        : styles.accent === "orange"
                        ? "border-orange-200/50"
                        : styles.accent === "cyan"
                        ? "border-cyan-200/50"
                        : styles.accent === "teal"
                        ? "border-teal-200/50"
                        : "border-gray-200/50"
                    }`}
                  >
                    <div className="flex gap-1.5 items-center mb-2">
                      <Car size={14} className={styles.text} />
                      <span
                        className={`text-[10px] font-semibold tracking-wide ${styles.text} uppercase`}
                      >
                        Trip Details
                      </span>
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">From</span>
                        <span className="font-semibold text-right text-gray-900">
                          {purchasedData.departureCity},{" "}
                          {purchasedData.departureRegion}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">To</span>
                        <span className="font-semibold text-right text-gray-900">
                          {purchasedData.destinationCity},{" "}
                          {purchasedData.destinationRegion}
                        </span>
                      </div>
                      {purchasedData.vehicleType && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Vehicle</span>
                          <span className="font-semibold text-right text-gray-900">
                            {purchasedData.vehicleType}
                          </span>
                        </div>
                      )}
                      {purchasedData.busLicensePlate && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Plate</span>
                          <span className="font-mono font-semibold text-right text-gray-900">
                            {purchasedData.busLicensePlate}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Coverage Benefits for travel_insurance with packages */}
              {hasStringDuration &&
                purchasedData.insurance_coverage_list &&
                purchasedData.insurance_coverage_list.length > 0 && (
                  <div
                    className={`hidden md:block p-2 md:p-3 ${styles.bg} rounded-lg md:rounded-xl border ${
                      styles.accent === "blue"
                        ? "border-blue-200/50"
                        : styles.accent === "indigo"
                        ? "border-indigo-200/50"
                        : styles.accent === "purple"
                        ? "border-purple-200/50"
                        : styles.accent === "pink"
                        ? "border-pink-200/50"
                        : styles.accent === "red"
                        ? "border-red-200/50"
                        : styles.accent === "orange"
                        ? "border-orange-200/50"
                        : styles.accent === "cyan"
                        ? "border-cyan-200/50"
                        : styles.accent === "teal"
                        ? "border-teal-200/50"
                        : "border-gray-200/50"
                    }`}
                  >
                    <div className="flex gap-1.5 items-center mb-2">
                      <Shield size={14} className={styles.text} />
                      <span
                        className={`text-[10px] font-semibold tracking-wide ${styles.text} uppercase`}
                      >
                        Coverage Benefits
                      </span>
                    </div>
                    <div className="space-y-1">
                      {purchasedData.insurance_coverage_list
                        .slice(0, 2)
                        .map((benefit, idx) => (
                          <div key={idx} className="flex gap-1.5 items-start">
                            <Check
                              size={12}
                              className="flex-shrink-0 mt-0.5 text-emerald-600"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {benefit.title}
                              </p>
                              {benefit.limit && (
                                <p className="text-[10px] font-semibold text-emerald-600">
                                  Limit: ₵{benefit.limit}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      {purchasedData.insurance_coverage_list.length > 2 && (
                        <p className="text-[10px] text-gray-500 mt-1">
                          +{purchasedData.insurance_coverage_list.length - 2}{" "}
                          more benefits
                        </p>
                      )}
                    </div>
                  </div>
                )}
            </>
          )}

          {/* Life Insurance Details */}
          {isLifeInsurance && purchasedData && (
            <>
              <div className="grid grid-cols-2 gap-1.5 md:gap-3">
                <div
                  className={`p-1.5 md:p-3 ${styles.bg} rounded-md md:rounded-xl border ${
                    styles.accent === "blue"
                      ? "border-blue-200/50"
                      : styles.accent === "indigo"
                      ? "border-indigo-200/50"
                      : styles.accent === "purple"
                      ? "border-purple-200/50"
                      : styles.accent === "pink"
                      ? "border-pink-200/50"
                      : styles.accent === "red"
                      ? "border-red-200/50"
                      : styles.accent === "orange"
                      ? "border-orange-200/50"
                      : styles.accent === "cyan"
                      ? "border-cyan-200/50"
                      : styles.accent === "teal"
                      ? "border-teal-200/50"
                      : "border-gray-200/50"
                  }`}
                >
                  <div className="flex gap-1 items-center mb-0.5 md:gap-1.5 md:mb-1.5">
                    <Clock className={`w-3 h-3 md:w-3.5 md:h-3.5 ${styles.text}`} />
                    <span
                      className={`text-[9px] font-semibold tracking-wide ${styles.text} uppercase md:text-[10px]`}
                    >
                      Duration
                    </span>
                  </div>
                  <p className="text-[11px] font-bold text-gray-900 truncate md:text-sm">
                    {formatDurationLabel}
                  </p>
                </div>

                <div
                  className={`p-1.5 md:p-3 ${styles.bg} rounded-md md:rounded-xl border ${
                    styles.accent === "blue"
                      ? "border-blue-200/50"
                      : styles.accent === "indigo"
                      ? "border-indigo-200/50"
                      : styles.accent === "purple"
                      ? "border-purple-200/50"
                      : styles.accent === "pink"
                      ? "border-pink-200/50"
                      : styles.accent === "red"
                      ? "border-red-200/50"
                      : styles.accent === "orange"
                      ? "border-orange-200/50"
                      : styles.accent === "cyan"
                      ? "border-cyan-200/50"
                      : styles.accent === "teal"
                      ? "border-teal-200/50"
                      : "border-gray-200/50"
                  }`}
                >
                  <div className="flex gap-1 items-center mb-0.5 md:gap-1.5 md:mb-1.5">
                    <DollarSign className={`w-3 h-3 md:w-3.5 md:h-3.5 ${styles.text}`} />
                    <span
                      className={`text-[9px] font-semibold tracking-wide ${styles.text} uppercase md:text-[10px]`}
                    >
                      Premium
                    </span>
                  </div>
                  <p className={`text-[11px] font-bold truncate md:text-sm ${styles.text}`}>
                    {formatMoney(getPrice)}
                  </p>
                </div>
              </div>

              {purchasedData.description && (
                <div
                  className={`hidden md:block p-2 md:p-3 ${styles.bg} rounded-lg md:rounded-xl border ${
                    styles.accent === "blue"
                      ? "border-blue-200/50"
                      : styles.accent === "indigo"
                      ? "border-indigo-200/50"
                      : styles.accent === "purple"
                      ? "border-purple-200/50"
                      : styles.accent === "pink"
                      ? "border-pink-200/50"
                      : styles.accent === "red"
                      ? "border-red-200/50"
                      : styles.accent === "orange"
                      ? "border-orange-200/50"
                      : styles.accent === "cyan"
                      ? "border-cyan-200/50"
                      : styles.accent === "teal"
                      ? "border-teal-200/50"
                      : "border-gray-200/50"
                  }`}
                >
                  <div className="flex gap-1.5 items-center mb-2">
                    <FileText size={14} className={styles.text} />
                    <span
                      className={`text-[10px] font-semibold tracking-wide ${styles.text} uppercase`}
                    >
                      Description
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 line-clamp-3">
                    {purchasedData.description}
                  </p>
                </div>
              )}

              {/* Coverage Benefits */}
              {purchasedData.insurance_coverage_list &&
                purchasedData.insurance_coverage_list.length > 0 && (
                  <div
                    className={`hidden md:block p-2 md:p-3 ${styles.bg} rounded-lg md:rounded-xl border ${
                      styles.accent === "blue"
                        ? "border-blue-200/50"
                        : styles.accent === "indigo"
                        ? "border-indigo-200/50"
                        : styles.accent === "purple"
                        ? "border-purple-200/50"
                        : styles.accent === "pink"
                        ? "border-pink-200/50"
                        : styles.accent === "red"
                        ? "border-red-200/50"
                        : styles.accent === "orange"
                        ? "border-orange-200/50"
                        : styles.accent === "cyan"
                        ? "border-cyan-200/50"
                        : styles.accent === "teal"
                        ? "border-teal-200/50"
                        : "border-gray-200/50"
                    }`}
                  >
                    <div className="flex gap-1.5 items-center mb-2">
                      <Shield size={14} className={styles.text} />
                      <span
                        className={`text-[10px] font-semibold tracking-wide ${styles.text} uppercase`}
                      >
                        Coverage Benefits
                      </span>
                    </div>
                    <div className="space-y-1">
                      {purchasedData.insurance_coverage_list
                        .slice(0, 2)
                        .map((benefit, idx) => (
                          <div key={idx} className="flex gap-1.5 items-start">
                            <Check
                              size={12}
                              className="flex-shrink-0 mt-0.5 text-emerald-600"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {benefit.title}
                              </p>
                              {benefit.limit && (
                                <p className="text-[10px] font-semibold text-emerald-600">
                                  Limit: ₵{benefit.limit}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      {purchasedData.insurance_coverage_list.length > 2 && (
                        <p className="text-[10px] text-gray-500 mt-1">
                          +{purchasedData.insurance_coverage_list.length - 2}{" "}
                          more benefits
                        </p>
                      )}
                    </div>
                  </div>
                )}
            </>
          )}

          {/* General Insurance Details */}
          {isGeneralInsurance && purchasedData && (
            <>
              <div className="grid grid-cols-2 gap-1.5 md:gap-3">
                <div
                  className={`p-1.5 md:p-3 ${styles.bg} rounded-md md:rounded-xl border ${
                    styles.accent === "blue"
                      ? "border-blue-200/50"
                      : styles.accent === "indigo"
                      ? "border-indigo-200/50"
                      : styles.accent === "purple"
                      ? "border-purple-200/50"
                      : styles.accent === "pink"
                      ? "border-pink-200/50"
                      : styles.accent === "red"
                      ? "border-red-200/50"
                      : styles.accent === "orange"
                      ? "border-orange-200/50"
                      : styles.accent === "cyan"
                      ? "border-cyan-200/50"
                      : styles.accent === "teal"
                      ? "border-teal-200/50"
                      : "border-gray-200/50"
                  }`}
                >
                  <div className="flex gap-1 items-center mb-0.5 md:gap-1.5 md:mb-1.5">
                    <Clock className={`w-3 h-3 md:w-3.5 md:h-3.5 ${styles.text}`} />
                    <span
                      className={`text-[9px] font-semibold tracking-wide ${styles.text} uppercase md:text-[10px]`}
                    >
                      Duration
                    </span>
                  </div>
                  <p className="text-[11px] font-bold text-gray-900 truncate md:text-sm">
                    {formatDurationLabel}
                  </p>
                </div>

                <div
                  className={`p-1.5 md:p-3 ${styles.bg} rounded-md md:rounded-xl border ${
                    styles.accent === "blue"
                      ? "border-blue-200/50"
                      : styles.accent === "indigo"
                      ? "border-indigo-200/50"
                      : styles.accent === "purple"
                      ? "border-purple-200/50"
                      : styles.accent === "pink"
                      ? "border-pink-200/50"
                      : styles.accent === "red"
                      ? "border-red-200/50"
                      : styles.accent === "orange"
                      ? "border-orange-200/50"
                      : styles.accent === "cyan"
                      ? "border-cyan-200/50"
                      : styles.accent === "teal"
                      ? "border-teal-200/50"
                      : "border-gray-200/50"
                  }`}
                >
                  <div className="flex gap-1 items-center mb-0.5 md:gap-1.5 md:mb-1.5">
                    <DollarSign className={`w-3 h-3 md:w-3.5 md:h-3.5 ${styles.text}`} />
                    <span
                      className={`text-[9px] font-semibold tracking-wide ${styles.text} uppercase md:text-[10px]`}
                    >
                      Premium
                    </span>
                  </div>
                  <p className={`text-[11px] font-bold truncate md:text-sm ${styles.text}`}>
                    {formatMoney(getPrice)}
                  </p>
                </div>
              </div>

              {purchasedData.description && (
                <div
                  className={`hidden md:block p-2 md:p-3 ${styles.bg} rounded-lg md:rounded-xl border ${
                    styles.accent === "blue"
                      ? "border-blue-200/50"
                      : styles.accent === "indigo"
                      ? "border-indigo-200/50"
                      : styles.accent === "purple"
                      ? "border-purple-200/50"
                      : styles.accent === "pink"
                      ? "border-pink-200/50"
                      : styles.accent === "red"
                      ? "border-red-200/50"
                      : styles.accent === "orange"
                      ? "border-orange-200/50"
                      : styles.accent === "cyan"
                      ? "border-cyan-200/50"
                      : styles.accent === "teal"
                      ? "border-teal-200/50"
                      : "border-gray-200/50"
                  }`}
                >
                  <div className="flex gap-1.5 items-center mb-2">
                    <FileText size={14} className={styles.text} />
                    <span
                      className={`text-[10px] font-semibold tracking-wide ${styles.text} uppercase`}
                    >
                      Description
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 line-clamp-3">
                    {purchasedData.description}
                  </p>
                </div>
              )}

              {/* Coverage Benefits */}
              {purchasedData.insurance_coverage_list &&
                purchasedData.insurance_coverage_list.length > 0 && (
                  <div
                    className={`hidden md:block p-2 md:p-3 ${styles.bg} rounded-lg md:rounded-xl border ${
                      styles.accent === "blue"
                        ? "border-blue-200/50"
                        : styles.accent === "indigo"
                        ? "border-indigo-200/50"
                        : styles.accent === "purple"
                        ? "border-purple-200/50"
                        : styles.accent === "pink"
                        ? "border-pink-200/50"
                        : styles.accent === "red"
                        ? "border-red-200/50"
                        : styles.accent === "orange"
                        ? "border-orange-200/50"
                        : styles.accent === "cyan"
                        ? "border-cyan-200/50"
                        : styles.accent === "teal"
                        ? "border-teal-200/50"
                        : "border-gray-200/50"
                    }`}
                  >
                    <div className="flex gap-1.5 items-center mb-2">
                      <Shield size={14} className={styles.text} />
                      <span
                        className={`text-[10px] font-semibold tracking-wide ${styles.text} uppercase`}
                      >
                        Coverage Benefits
                      </span>
                    </div>
                    <div className="space-y-1">
                      {purchasedData.insurance_coverage_list
                        .slice(0, 2)
                        .map((benefit, idx) => (
                          <div key={idx} className="flex gap-1.5 items-start">
                            <Check
                              size={12}
                              className="flex-shrink-0 mt-0.5 text-emerald-600"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {benefit.title}
                              </p>
                              {benefit.limit && (
                                <p className="text-[10px] font-semibold text-emerald-600">
                                  Limit: ₵{benefit.limit}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      {purchasedData.insurance_coverage_list.length > 2 && (
                        <p className="text-[10px] text-gray-500 mt-1">
                          +{purchasedData.insurance_coverage_list.length - 2}{" "}
                          more benefits
                        </p>
                      )}
                    </div>
                  </div>
                )}
            </>
          )}

          <PolicyDatesInfo policy={policy} compact />
        </div>

        {/* Footer – status badge with bold, status-specific colors */}
        <div className="pt-3 mt-3 border-t border-gray-100 md:pt-4 md:mt-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center min-w-0">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold ring-2 md:px-3 md:py-1.5 md:text-xs ${statusBadgeStyles.bg} ${statusBadgeStyles.text} ${statusBadgeStyles.ring}`}
              >
                <span
                  className={`relative inline-block w-2 h-2 rounded-full ${statusBadgeStyles.dot}`}
                >
                  {isActive && (
                    <span
                      className="absolute inset-0 rounded-full opacity-60 animate-ping bg-emerald-400"
                    />
                  )}
                </span>
                {isCancelPending ? (
                  <>
                    <span className="md:hidden">Pending</span>
                    <span className="hidden md:inline">Cancel Pending</span>
                  </>
                ) : isActive ? (
                  "Active"
                ) : isCancelled ? (
                  "Cancelled"
                ) : (
                  "Inactive"
                )}
              </span>
            </div>
            <div className="flex gap-1 items-center text-xs text-gray-500">
              <CheckCircle size={12} className="text-emerald-500" />
              <span className="hidden sm:inline">Protected</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PolicyCard;
